import { useLanguage } from "@/contexts/LanguageContext";
import { LeadForm } from "@/components/LeadForm";
import { Badge } from "@/components/ui/badge";
import { Skeleton } from "@/components/ui/skeleton";
import { useDocumentMeta } from "@/hooks/useDocumentMeta";
import { SITE } from "@/lib/site";
import { trpc } from "@/lib/trpc";
import { Bath, BedDouble, House, MapPin, Ruler, Users } from "lucide-react";
import { Link, useParams } from "wouter";
function parse<T>(value: string, fallback: T): T {
  try {
    return JSON.parse(value) as T;
  } catch {
    return fallback;
  }
}
export default function StayDetail() {
  const {
    tx,
    tf,
    href: localeHref,
    dateLocale
  } = useLanguage();
  const {
    slug = ""
  } = useParams<{
    slug: string;
  }>();
  const {
    data,
    isLoading
  } = trpc.public.stays.bySlug.useQuery({
    slug
  });
  useDocumentMeta(data ? `${data.title} | CYPRUS PRIVÉ STAY` : "Unterkunft | CYPRUS PRIVÉ STAY", data?.shortDescription ?? "Ausgewählte Unterkunft in Nordzypern persönlich anfragen.");
  if (isLoading) return <main className="container py-24"><Skeleton className="h-[600px] rounded-none" /></main>;
  if (!data) return <main className="container py-24 text-center"><p className="eyebrow">{tx("NICHT GEFUNDEN")}</p><h1 className="mt-4 display-md font-semibold">{tx("DIESE UNTERKUNFT IST NICHT VERFÜGBAR.")}</h1><Link href="/stay" className="mt-6 inline-block text-sm font-bold text-[#A98950]">{tx("ZURÜCK ZU STAY")}</Link></main>;
  const images = parse<string[]>(data.imageUrlsJson, []);
  const gallery = images.length ? images : [data.coverImageUrl ?? SITE.images.stay];
  const amenities = parse<string[]>(data.amenitiesJson, []);
  const rules = parse<string[]>(data.houseRulesJson, []);
  const seasons = parse<{
    from: string;
    to: string;
    price: string;
    minimumStay?: number;
  }[]>(data.seasonPricesJson, []);
  const schema = {
    "@context": "https://schema.org",
    "@type": "VacationRental",
    name: data.title,
    description: data.shortDescription,
    image: gallery,
    address: {
      "@type": "PostalAddress",
      addressLocality: data.city || data.region,
      addressRegion: data.region
    },
    occupancy: {
      "@type": "QuantitativeValue",
      maxValue: data.guests
    },
    numberOfBedrooms: data.bedrooms
  };
  return <main>
    <script type="application/ld+json" dangerouslySetInnerHTML={{
      __html: JSON.stringify(schema)
    }} />
    <section className="bg-[#FFFDF9] py-12"><div className="container"><p className="flex items-center gap-2 text-[10px] font-bold tracking-[.14em] text-[#A98950]"><MapPin className="h-4 w-4" />{tx(data.region)}{tx(data.city ? ` · ${data.city}` : "")}</p><h1 className="mt-3 display-md font-semibold">{tx(data.title)}</h1><p className="mt-4 max-w-3xl leading-8 text-[#66615A]">{tx(data.shortDescription)}</p></div></section>
    <section className="container grid gap-3 pb-20 lg:grid-cols-[1.6fr_.8fr]"><img src={gallery[0]} alt={tf("{0} – Hauptansicht", [data.title])} className="h-full min-h-[520px] w-full object-cover" />{tx(gallery.length > 1 ? <div className="grid gap-3">{tx(gallery.slice(1, 3).map((image, index) => <img key={image} src={image} alt={tf("{0} – Ansicht {1}", [data.title, index + 2])} className="h-full min-h-[250px] w-full object-cover" />))}</div> : <img src={SITE.images.stay} alt={tx("Mediterranes Ambiente")} className="h-full min-h-[320px] w-full object-cover" />)}</section>
    <section className="paper-grain py-20"><div className="container grid gap-12 lg:grid-cols-[1fr_.38fr]"><div><div className="grid grid-cols-2 gap-px bg-[#DED6C8] sm:grid-cols-5">{tx([{
              icon: Users,
              label: `${data.guests} Gäste`
            }, {
              icon: BedDouble,
              label: `${data.bedrooms} Schlafzimmer`
            }, {
              icon: Bath,
              label: `${data.bathrooms} Bäder`
            }, {
              icon: House,
              label: `${data.beds} Betten`
            }, {
              icon: Ruler,
              label: data.areaSqm ? `${data.areaSqm} m²` : "Fläche auf Anfrage"
            }].map(item => <div key={item.label} className="bg-[#FFFDF9] p-5 text-center"><item.icon className="mx-auto h-5 w-5 text-[#A98950]" /><p className="mt-3 text-xs font-semibold">{tx(item.label)}</p></div>))}</div><h2 className="mt-14 text-4xl font-semibold">{tx("Über diese Unterkunft")}</h2><p className="mt-5 whitespace-pre-line leading-8 text-[#66615A]">{tx(data.description)}</p>{tx(amenities.length > 0 && <><h2 className="mt-14 text-4xl font-semibold">{tx("Ausstattung")}</h2><div className="mt-6 grid grid-cols-2 gap-3 sm:grid-cols-3">{tx(amenities.map(item => <div key={item} className="border border-[#DED6C8] bg-white p-4 text-sm">{tx(item)}</div>))}</div></>)}{tx(rules.length > 0 && <><h2 className="mt-14 text-4xl font-semibold">{tx("Hausregeln")}</h2><ul className="mt-5 space-y-2 text-sm leading-7 text-[#66615A]">{tx(rules.map(item => <li key={item}>— {tx(item)}</li>))}</ul></>)}</div><aside className="h-fit border border-[#DED6C8] bg-[#FFFDF9] p-7 lg:sticky lg:top-28"><p className="eyebrow">{tx("AUFENTHALT")}</p><p className="mt-4 font-display text-3xl">{tx(data.pricePerNight ? `ab ${Number(data.pricePerNight).toLocaleString(dateLocale)} €` : "Preis auf Anfrage")}</p>{tx(data.pricePerNight && <p className="text-xs text-[#66615A]">{tx("pro Nacht · zzgl. ggf. angezeigter Gebühren")}</p>)}<dl className="mt-7 space-y-4 border-y border-[#DED6C8] py-5 text-sm"><div className="flex justify-between gap-3"><dt className="text-[#66615A]">{tx("Mindestaufenthalt")}</dt><dd>{tx(data.minimumStay)}{tx(" Nächte")}</dd></div><div className="flex justify-between gap-3"><dt className="text-[#66615A]">{tx("Check-in")}</dt><dd>{tx(data.checkIn ?? "nach Absprache")}</dd></div><div className="flex justify-between gap-3"><dt className="text-[#66615A]">{tx("Check-out")}</dt><dd>{tx(data.checkOut ?? "nach Absprache")}</dd></div></dl>{tx(seasons.length > 0 && <div className="mt-6"><p className="text-xs font-bold tracking-[.1em]">{tx("SAISONPREISE")}</p>{tx(seasons.map(season => <div key={`${season.from}-${season.to}`} className="mt-3 flex justify-between gap-3 text-xs"><span>{tx(season.from)}–{tx(season.to)}</span><span>{tx(season.price)}</span></div>))}</div>)}<Badge variant="outline" className="mt-6 rounded-none border-[#A98950] text-[#A98950]">{tx("ANFRAGE ERFORDERLICH")}</Badge></aside></div></section>
    <section className="bg-[#EEE7DB] py-24"><div className="container"><div className="mx-auto max-w-5xl"><LeadForm kind="stay" title={tx("VERFÜGBARKEIT ANFRAGEN")} intro={tf("Deine Anfrage für „{0}“ wird persönlich geprüft und bestätigt.", [data.title])} initialInterest="stay" stayPropertyId={data.id} /></div></div></section>
  </main>;
}
